import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import SharePointUpload from '../components/SharePointUpload';

const STATUS_COLORS = { pending:'badge-blue', in_review:'badge-gold', completed:'badge-green', overdue:'badge-red', returned:'badge-red', archived:'badge-gray' };

export default function DraftingDesk({ showToast, currentUser, editMode }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [turnaround, setTurnaround] = useState([]);
  const [form, setForm] = useState({ document_type:'', priority:'medium', department:'', date_required:'', counterparty:'', description:'' });

  useEffect(() => { loadDrafts(); loadTurnaround(); }, []);

  async function loadDrafts() {
    try {
      const { data } = await api.getRequests({ type: 'contract_draft' });
      setDrafts(data.data || []);
    } catch { showToast('Failed to load drafts'); }
    finally { setLoading(false); }
  }

  async function loadTurnaround() {
    try { const { data } = await api.getDraftTurnaround(); setTurnaround(data || []); } catch { /* silent */ }
  }

  async function submitDraft() {
    if (!form.document_type) return showToast('Please select a document type');
    try {
      await api.createRequest({
        subject: `${form.document_type}${form.counterparty ? ' — ' + form.counterparty : ''}`,
        type: 'contract_draft', description: form.description,
        priority: form.priority, date_required: form.date_required,
        requester_dept: form.department,
      });
      showToast('Draft request submitted successfully');
      setShowForm(false);
      setForm({ document_type:'', priority:'medium', department:'', date_required:'', counterparty:'', description:'' });
      loadDrafts();
    } catch (err) { showToast('Submission failed: ' + (err.response?.data?.error || err.message)); }
  }

  async function updateStatus(id, status) {
    try { await api.updateRequest(id, { status }); showToast('Updated'); loadDrafts(); setSelected(null); }
    catch { showToast('Update failed'); }
  }

  const badge = (s) => <span className={`badge ${STATUS_COLORS[s]||'badge-gray'}`}><span className="dot"></span>{s?.replace(/_/g,' ')}</span>;
  const priorityBadge = (p) => { const c={low:'badge-gray',medium:'badge-gold',high:'badge-red',urgent:'badge-red'}; return <span className={`badge ${c[p]||'badge-gray'}`}>{p}</span>; };

  const active = drafts.filter(d => d.status !== 'completed' && d.status !== 'archived');
  const completed = drafts.filter(d => d.status === 'completed');

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Drafting Desk</div>
        <button className="topbar-btn btn-primary" onClick={() => setShowForm(true)}>+ New Draft Request</button>
      </div>

      {/* KPI row */}
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-icon blue">✏️</div><div><div className="stat-num">{drafts.filter(d=>d.status==='pending').length}</div><div className="stat-label">Pending</div></div></div>
        <div className="stat-card"><div className="stat-icon gold">🔍</div><div><div className="stat-num">{drafts.filter(d=>d.status==='in_review').length}</div><div className="stat-label">Under Review</div></div></div>
        <div className="stat-card"><div className="stat-icon green">✅</div><div><div className="stat-num">{completed.length}</div><div className="stat-label">Completed</div></div></div>
        <div className="stat-card"><div className="stat-icon red">⏰</div><div><div className="stat-num">{drafts.filter(d=>d.status==='overdue').length}</div><div className="stat-label">Overdue</div></div></div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:12}}>
        {['queue','completed','sla'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`topbar-btn ${activeTab===t?'btn-primary':'btn-ghost'}`}>
            {t==='queue'?'📂 Active Queue':t==='completed'?'✅ Completed':'⏱ SLA Targets'}
          </button>
        ))}
      </div>

      <div className="row-2">
        <div className="card" style={{flex:2}}>
          {loading ? <div className="loading">Loading...</div> : activeTab === 'queue' ? (
            <>
              <div className="card-title">📂 Active Draft Queue ({active.length})</div>
              {editMode ? (
                <EditableDataTable
                  columns={[{key:'ref',label:'Ref'},{key:'subject',label:'Document'},{key:'requester_name',label:'Requester'},{key:'priority',label:'Priority'},{key:'status',label:'Status'}]}
                  data={active}
                  editable={true}
                  onDataChange={(updated) => setDrafts([...updated, ...completed])}
                  renderCell={(row, colKey) => {
                    if (colKey==='ref') return <span style={{fontWeight:600,color:'var(--mid)',fontSize:10}}>{row.ref}</span>;
                    if (colKey==='priority') return priorityBadge(row.priority);
                    if (colKey==='status') return badge(row.status);
                    return row[colKey]??'';
                  }}
                />
              ) : (
                <table>
                  <thead><tr><th>Ref</th><th>Document</th><th>Department</th><th>Priority</th><th>Date Required</th><th>Status</th></tr></thead>
                  <tbody>
                    {active.map(d => (
                      <tr key={d.id} style={{cursor:'pointer', background: selected?.id===d.id?'var(--pale)':''}}
                        onClick={() => setSelected(selected?.id===d.id ? null : d)}>
                        <td style={{fontWeight:600,color:'var(--mid)',fontSize:10}}>{d.ref}</td>
                        <td>{d.subject}</td>
                        <td style={{fontSize:12}}>{d.requester_dept||'—'}</td>
                        <td>{priorityBadge(d.priority)}</td>
                        <td style={{fontSize:12}}>{d.date_required ? new Date(d.date_required).toLocaleDateString('en-GB') : '—'}</td>
                        <td>{badge(d.status)}</td>
                      </tr>
                    ))}
                    {active.length===0 && <tr><td colSpan={6} style={{textAlign:'center',color:'var(--muted)'}}>No active draft requests</td></tr>}
                  </tbody>
                </table>
              )}
            </>
          ) : activeTab === 'completed' ? (
            <>
              <div className="card-title">✅ Completed Drafts ({completed.length})</div>
              <table>
                <thead><tr><th>Ref</th><th>Document</th><th>Requester</th><th>Completed</th></tr></thead>
                <tbody>
                  {completed.map(d => (
                    <tr key={d.id}>
                      <td style={{fontWeight:600,color:'var(--mid)',fontSize:10}}>{d.ref}</td>
                      <td>{d.subject}</td>
                      <td style={{fontSize:12}}>{d.requester_name||'—'}</td>
                      <td style={{fontSize:12}}>{d.updated_at ? new Date(d.updated_at).toLocaleDateString('en-GB') : '—'}</td>
                    </tr>
                  ))}
                  {completed.length===0 && <tr><td colSpan={4} style={{textAlign:'center',color:'var(--muted)'}}>No completed drafts</td></tr>}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <div className="card-title">⏱ SLA Turnaround Targets</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:8}}>
                {[{val:'3',label:'days — NDA'},{val:'7',label:'days — Full Contract'},{val:'2',label:'days — LOI / MOU'},{val:'5',label:'days — Employment Contract'},{val:'3',label:'days — Board Resolution'},{val:'10',label:'days — Power of Attorney'}].map((t,i) => (
                  <div key={i} style={{textAlign:'center',padding:14,background:'var(--pale)',borderRadius:8}}>
                    <div style={{fontSize:28,fontWeight:700,color:'var(--mid)'}}>{t.val}</div>
                    <div style={{fontSize:11,color:'var(--muted)'}}>{t.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="card" style={{flex:1}}>
            <div className="card-title">📄 {selected.ref}</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Document</div><div style={{fontWeight:600}}>{selected.subject}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Status</div>{badge(selected.status)}</div>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Priority</div>{priorityBadge(selected.priority)}</div>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Department</div><div style={{fontSize:13}}>{selected.requester_dept||'—'}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Date Required</div><div style={{fontSize:13}}>{selected.date_required ? new Date(selected.date_required).toLocaleDateString('en-GB') : '—'}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Description</div><div style={{fontSize:13,lineHeight:1.5}}>{selected.description||'—'}</div></div>
              <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                {selected.status==='pending' && <button className="sig-btn primary" onClick={()=>updateStatus(selected.id,'in_review')}>Accept</button>}
                {selected.status==='in_review' && <button className="sig-btn primary" onClick={()=>updateStatus(selected.id,'completed')}>Mark Complete</button>}
                {(selected.status==='pending'||selected.status==='in_review') && <button className="sig-btn" onClick={()=>updateStatus(selected.id,'overdue')}>Flag Overdue</button>}
                <button className="sig-btn ghost" onClick={()=>setSelected(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <SharePointUpload linkedEntityType="request" showToast={showToast} />

      {/* New Draft Modal */}
      {showForm && (
        <div className="modal-overlay open" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New Draft Request</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Document Type *</label>
                <select value={form.document_type} onChange={e => setForm({...form, document_type: e.target.value})}>
                  <option value="">-- Select --</option>
                  <option>Contract Agreement</option>
                  <option>Memorandum of Understanding (MOU)</option>
                  <option>Non-Disclosure Agreement (NDA)</option>
                  <option>Employment Contract</option>
                  <option>Subcontract Agreement</option>
                  <option>Letter of Intent (LOI)</option>
                  <option>Service Level Agreement (SLA)</option>
                  <option>Tenancy / Lease Agreement</option>
                  <option>Power of Attorney</option>
                  <option>Board Resolution</option>
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label>Requesting Department</label>
                <input type="text" value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="e.g. Projects, Finance, HR" />
              </div>
              <div className="form-group">
                <label>Date Required</label>
                <input type="date" value={form.date_required} onChange={e => setForm({...form, date_required: e.target.value})} />
              </div>
              <div className="form-group full">
                <label>Counterparty / Other Party</label>
                <input type="text" value={form.counterparty} onChange={e => setForm({...form, counterparty: e.target.value})} placeholder="Company or individual name" />
              </div>
              <div className="form-group full">
                <label>Description / Key Terms</label>
                <textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Scope, parties, key terms, special clauses, governing law..." />
              </div>
            </div>
            <div className="form-actions">
              <button className="topbar-btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="topbar-btn btn-primary" onClick={submitDraft} disabled={!form.document_type}>Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
